import React, { useState, useCallback, useEffect, useRef } from 'react'
import { cn } from '../lib/utils'
import {
  Flame,
  Rocket,
  Wallet,
  Ticket,
  FileText,
  Vote,
  CheckCircle,
  Zap,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Info,
  Coins,
  TrendingUp
} from 'lucide-react'
import { useJitoAPY } from '../hooks/useJitoAPY'

/**
 * Step data interface defining the structure for each step in the infographic
 * Includes visual styling properties and content
 */
interface StepData {
  id: number
  icon: React.ElementType
  title: string
  shortTitle: string
  description: string
  details: string[]
  color: 'cyan' | 'magenta' | 'purple' | 'green'
  emoji: string
}

/**
 * Color configuration for each step type
 * Provides consistent styling across the component
 */
const colorConfig = {
  cyan: {
    primary: 'text-cyber-cyan',
    bg: 'bg-cyber-cyan/10',
    border: 'border-cyber-cyan/30',
    hoverBorder: 'hover:border-cyber-cyan/60',
    shadow: 'shadow-neon-cyan',
    gradient: 'from-cyber-cyan/20 to-transparent',
    iconBg: 'bg-cyber-cyan/20',
    dotBg: 'bg-cyber-cyan',
    lineBg: 'bg-gradient-to-b from-cyber-cyan to-transparent'
  },
  magenta: {
    primary: 'text-cyber-magenta',
    bg: 'bg-cyber-magenta/10',
    border: 'border-cyber-magenta/30',
    hoverBorder: 'hover:border-cyber-magenta/60',
    shadow: 'shadow-neon-magenta',
    gradient: 'from-cyber-magenta/20 to-transparent',
    iconBg: 'bg-cyber-magenta/20',
    dotBg: 'bg-cyber-magenta',
    lineBg: 'bg-gradient-to-b from-cyber-magenta to-transparent'
  },
  purple: {
    primary: 'text-cyber-purple',
    bg: 'bg-cyber-purple/10',
    border: 'border-cyber-purple/30',
    hoverBorder: 'hover:border-cyber-purple/60',
    shadow: 'shadow-neon-purple',
    gradient: 'from-cyber-purple/20 to-transparent',
    iconBg: 'bg-cyber-purple/20',
    dotBg: 'bg-cyber-purple',
    lineBg: 'bg-gradient-to-b from-cyber-purple to-transparent'
  },
  green: {
    primary: 'text-cyber-green',
    bg: 'bg-cyber-green/10',
    border: 'border-cyber-green/30',
    hoverBorder: 'hover:border-cyber-green/60',
    shadow: 'shadow-neon-green',
    gradient: 'from-cyber-green/20 to-transparent',
    iconBg: 'bg-cyber-green/20',
    dotBg: 'bg-cyber-green',
    lineBg: 'bg-gradient-to-b from-cyber-green to-transparent'
  }
}

/**
 * Individual step card component
 * Handles the expandable content and interactive hover effects
 */
interface StepCardProps {
  step: StepData
  isExpanded: boolean
  onToggle: () => void
  index: number
  isReducedMotion: boolean
}

/**
 * StepCard Component
 * Renders an individual step in the how-it-works infographic
 * Features expandable details section and accessibility support
 */
const StepCard: React.FC<StepCardProps> = ({
  step,
  isExpanded,
  onToggle,
  index,
  isReducedMotion
}) => {
  const config = colorConfig[step.color]
  const Icon = step.icon

  return (
    <div
      className={cn(
        'relative group',
        !isReducedMotion && 'animate-fade-in'
      )}
      style={{ animationDelay: isReducedMotion ? '0ms' : `${index * 100}ms` }}
    >
      {/* Step card container */}
      <div
        className={cn(
          'glass-card p-4 sm:p-6 cursor-pointer transition-all duration-300',
          'border',
          config.border,
          config.hoverBorder,
          isExpanded && config.shadow,
          !isReducedMotion && 'hover:transform hover:scale-[1.02] hover:-translate-y-1'
        )}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`step-details-${step.id}`}
        aria-label={`Step ${step.id}: ${step.title}. ${isExpanded ? 'Click to collapse' : 'Click to expand for details'}`}
      >
        {/* Step header */}
        <div className="flex items-start gap-4">
          {/* Step number and icon */}
          <div className="flex-shrink-0">
            <div
              className={cn(
                'w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center relative',
                config.iconBg,
                'border',
                config.border,
                !isReducedMotion && 'group-hover:animate-pulse-glow'
              )}
            >
              {/* Step number badge */}
              <div
                className={cn(
                  'absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center',
                  'text-xs font-bold bg-dark-surface border',
                  config.border,
                  config.primary
                )}
              >
                {step.id}
              </div>
              {/* Icon */}
              <Icon
                className={cn(
                  'w-6 h-6 sm:w-7 sm:h-7',
                  config.primary,
                  !isReducedMotion && 'group-hover:scale-110 transition-transform duration-300'
                )}
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title with emoji */}
            <h3
              className={cn(
                'font-header text-lg sm:text-xl font-semibold mb-2 flex items-center gap-2 flex-wrap',
                config.primary
              )}
            >
              <span className="text-xl sm:text-2xl" role="img" aria-hidden="true">
                {step.emoji}
              </span>
              <span className="hidden sm:inline">{step.title}</span>
              <span className="sm:hidden">{step.shortTitle}</span>
            </h3>

            {/* Description */}
            <p className="text-text-secondary text-sm sm:text-base leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Expand/collapse indicator */}
          <div
            className={cn(
              'flex-shrink-0 p-2 rounded-lg transition-all duration-300',
              config.bg,
              isExpanded && 'rotate-180'
            )}
            aria-hidden="true"
          >
            <ChevronDown className={cn('w-5 h-5', config.primary)} />
          </div>
        </div>

        {/* Expandable details section */}
        <div
          id={`step-details-${step.id}`}
          className={cn(
            'overflow-hidden transition-all duration-300',
            isExpanded ? 'max-h-96 mt-4 opacity-100' : 'max-h-0 opacity-0'
          )}
          aria-hidden={!isExpanded}
        >
          <div className={cn('border-t pt-4', config.border)}>
            <h4 className={cn('text-sm font-semibold mb-3 flex items-center gap-2', config.primary)}>
              <Info className="w-4 h-4" aria-hidden="true" />
              Details
            </h4>
            <ul className="space-y-2">
              {step.details.map((detail, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-text-secondary text-sm"
                >
                  <ArrowRight
                    className={cn('w-4 h-4 flex-shrink-0 mt-0.5', config.primary)}
                    aria-hidden="true"
                  />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Animated connecting line between steps
 * Creates visual flow in the infographic
 */
interface ConnectorLineProps {
  color: 'cyan' | 'magenta' | 'purple' | 'green'
  isReducedMotion: boolean
  delay: number
}

/**
 * ConnectorLine Component
 * Renders an animated vertical line connecting adjacent steps
 */
const ConnectorLine: React.FC<ConnectorLineProps> = ({ color, isReducedMotion, delay }) => {
  const config = colorConfig[color]

  return (
    <div
      className={cn(
        'relative h-8 sm:h-12 flex justify-center',
        !isReducedMotion && 'animate-fade-in'
      )}
      style={{ animationDelay: isReducedMotion ? '0ms' : `${delay}ms` }}
      aria-hidden="true"
    >
      {/* Vertical line */}
      <div
        className={cn(
          'w-0.5 h-full',
          config.lineBg
        )}
      />
      {/* Animated dot */}
      <div
        className={cn(
          'absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full',
          config.dotBg,
          !isReducedMotion && 'animate-pulse'
        )}
      />
    </div>
  )
}

/**
 * Loop indicator for the final "Rinse & Repeat" step
 * Shows the cyclical nature of the process
 */
interface LoopIndicatorProps {
  isReducedMotion: boolean
}

/**
 * LoopIndicator Component
 * Displays an animated loop indicator at the end of the process
 */
const LoopIndicator: React.FC<LoopIndicatorProps> = ({ isReducedMotion }) => {
  return (
    <div
      className={cn(
        'mt-8 flex flex-col items-center gap-4',
        !isReducedMotion && 'animate-fade-in'
      )}
      style={{ animationDelay: isReducedMotion ? '0ms' : '1000ms' }}
      aria-hidden="true"
    >
      {/* Loop arrow */}
      <div className="relative">
        <div
          className={cn(
            'w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-dashed border-cyber-cyan/40',
            !isReducedMotion && 'animate-spin'
          )}
          style={{ animationDuration: '15s' }}
        />
        <RefreshCw
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-8 h-8 sm:w-10 sm:h-10 text-cyber-cyan',
            !isReducedMotion && 'animate-pulse'
          )}
        />
      </div>
      <p className="text-text-secondary text-sm text-center max-w-xs">
        <span className="text-cyber-cyan font-semibold">The cycle continues</span> until your
        token reaches its full potential!
      </p>
    </div>
  )
}

/**
 * Main HowItWorksInfographic component
 * Displays an interactive 9-step process for CTO Pools V2
 *
 * Features:
 * - Expandable step cards with detailed information
 * - Visual flow indicators connecting steps
 * - Animated icons and hover effects
 * - V2 Jito liquid staking integration explained
 * - Full accessibility support (ARIA, keyboard navigation)
 * - Respects prefers-reduced-motion preference
 * - Responsive design for all screen sizes
 */
export const HowItWorksInfographic: React.FC = () => {
  const { formattedApy } = useJitoAPY()
  // Track which steps are expanded
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set())

  // Check for reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  // Ref for keyboard navigation
  const containerRef = useRef<HTMLDivElement>(null)

  /**
   * The 9 steps of the CTO Pools V2 process
   * Updated to include Jito liquid staking integration
   * Each step includes engaging copy and detailed information
   */
  const stepsData: StepData[] = [
    {
      id: 1,
      icon: Flame,
      title: 'Community Sparks Revival',
      shortTitle: 'Spark Revival',
      description: 'The community identifies a "sleeping" Solana token ready for a legendary comeback.',
      details: [
        'Discover undervalued tokens with active communities',
        'Research project history and potential',
        'Rally community members who believe in the token\'s future'
      ],
      color: 'cyan',
      emoji: '🔥'
    },
    {
      id: 2,
      icon: Rocket,
      title: 'Launch a Pool',
      shortTitle: 'Launch Pool',
      description: 'Any believer can create a revival pool for free. Just pay gas! One click and you\'re in the game!',
      details: [
        'Connect your wallet and select the target token',
        'Only minimal gas fees required to launch',
        'Only one pool can be made per token to avoid fragmentation'
      ],
      color: 'magenta',
      emoji: '🚀'
    },
    {
      id: 3,
      icon: Wallet,
      title: 'Power Up with Liquid Staking',
      shortTitle: 'Fund & Stake',
      description: `Donate SOL to the pool—it's automatically staked via Jito to earn ${formattedApy} APY while waiting for proposals!`,
      details: [
        'Deposit any amount of SOL to the pool',
        'SOL is instantly converted to jitoSOL (liquid staking token)',
        `Your funds earn staking rewards (${formattedApy} APY) while in the pool`,
        'Receive proportional pool shares for governance',
        'Funds are only locked during active proposals'
      ],
      color: 'green',
      emoji: '💰'
    },
    {
      id: 4,
      icon: Ticket,
      title: 'Earn Proposal Rights',
      shortTitle: 'Earn Rights',
      description: 'Stake 1+ SOL for 24 hours to unlock proposal power. Pool creators get instant access!',
      details: [
        'Time delay protects the pool from malicious flash-loan attacks',
        'Pool creators automatically have proposal rights',
        'Demonstrates genuine commitment to the project'
      ],
      color: 'purple',
      emoji: '🎫'
    },
    {
      id: 5,
      icon: FileText,
      title: 'Submit a Proposal',
      shortTitle: 'Submit Proposal',
      description: 'Pitch your revival plan: marketing campaigns, dev bounties, exchange listings—sky\'s the limit!',
      details: [
        'Specify budget amount and recipient wallet',
        'Detail your plan for community review',
        'Small 1% fee is added to support the platform & burn CTOP tokens'
      ],
      color: 'cyan',
      emoji: '📋'
    },
    {
      id: 6,
      icon: Vote,
      title: 'Democratic Voting',
      shortTitle: 'Vote',
      description: 'Every donor votes! Your voting power = your pool share!',
      details: [
        'Vote Yes, No, or Abstain on proposals',
        'Voting power is capped at 20% to prevent whales from dominating',
        'Anti-whale mechanism ensures fair governance',
        'All votes recorded on-chain for transparency'
      ],
      color: 'magenta',
      emoji: '🗳️'
    },
    {
      id: 7,
      icon: CheckCircle,
      title: 'Reach Consensus',
      shortTitle: 'Consensus',
      description: 'Proposals need 30% participation AND majority approval to pass. Community consensus is king!',
      details: [
        'Quorum requirement ensures broad participation',
        'Simple majority determines outcome',
        'Results are final and immutable on-chain',
        'Voting lasts for 24 hours or until all donors have voted'
      ],
      color: 'green',
      emoji: '✅'
    },
    {
      id: 8,
      icon: Zap,
      title: 'Execute & Convert',
      shortTitle: 'Execute',
      description: 'jitoSOL converts back to SOL, funds go to the proposal wallet—plus auto-burn CTOP tokens!',
      details: [
        'jitoSOL is unstaked and converted back to SOL',
        'Permissionless execution after voting ends',
        'Half of 1% fee goes to CTO Pools development',
        'Half auto-buys and burns CTOP tokens!'
      ],
      color: 'purple',
      emoji: '⚡'
    },
    {
      id: 9,
      icon: RefreshCw,
      title: 'Rinse & Repeat',
      shortTitle: 'Repeat',
      description: 'Failed? Funds stay staked earning yield. Success? Keep the momentum—your pool keeps growing!',
      details: [
        'Failed proposals don\'t penalize contributors—funds keep earning',
        'Recovery proposals allow governance to recover funds if needed',
        'Successful pools can be the new backbone for the token\'s ecosystem',
        'Build sustainable momentum for your token'
      ],
      color: 'cyan',
      emoji: '🔄'
    }
  ]

  /**
   * Check user's motion preference on mount
   * Respects prefers-reduced-motion media query
   */
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  /**
   * Toggle step expansion state
   * Opens or closes the details section of a step
   */
  const toggleStep = useCallback((stepId: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev)
      if (next.has(stepId)) {
        next.delete(stepId)
      } else {
        next.add(stepId)
      }
      return next
    })
  }, [])

  /**
   * Expand all steps at once
   * Useful for users who want to see all details
   */
  const expandAll = useCallback(() => {
    setExpandedSteps(new Set(stepsData.map((s) => s.id)))
  }, [])

  /**
   * Collapse all steps at once
   * Returns to the compact view
   */
  const collapseAll = useCallback(() => {
    setExpandedSteps(new Set())
  }, [])

  const allExpanded = expandedSteps.size === stepsData.length

  return (
    <section
      className="py-16 px-4 bg-dark-surface/50"
      aria-labelledby="how-it-works-title"
      ref={containerRef}
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            id="how-it-works-title"
            className="text-4xl md:text-5xl font-bold font-header text-gradient-premium mb-6"
          >
            The Token Revival Journey
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-4">
            Your complete roadmap to breathing new life into abandoned tokens—
            <span className="text-cyber-green font-semibold">now with yield-earning liquid staking!</span>
          </p>
          <p className="text-sm text-text-muted">
            Click any step to learn more about how it works
          </p>
        </div>

        {/* V2 Liquid Staking Highlight */}
        <div
          className={cn(
            'mb-8 glass-card border border-cyber-green/30 p-6',
            !prefersReducedMotion && 'animate-fade-in'
          )}
        >
          <div className="flex items-start space-x-4">
            <div
              className="w-12 h-12 bg-cyber-green/20 rounded-lg flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <Coins className="w-6 h-6 text-cyber-green" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-cyber-green mb-2 font-header flex items-center gap-2">
                <TrendingUp className="w-5 h-5" aria-hidden="true" />
                V2: Jito Liquid Staking Integration
              </h3>
              <p className="text-text-secondary leading-relaxed text-sm sm:text-base">
                Pool funds are automatically staked via{' '}
                <span className="text-cyber-cyan font-semibold">Jito Stake Pool</span> to earn {formattedApy} APY. 
                When you donate SOL, it converts to jitoSOL—a liquid staking token that grows in value. 
                When proposals execute, jitoSOL converts back to SOL. 
                <span className="text-cyber-green font-semibold"> Your community's treasury grows while waiting!</span>
              </p>
            </div>
            <div className="flex-shrink-0 text-center hidden sm:block">
              <div className="text-2xl font-bold text-cyber-green">{formattedApy}</div>
              <div className="text-xs text-text-muted">APY</div>
            </div>
          </div>
        </div>

        {/* Expand/Collapse All Controls */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={allExpanded ? collapseAll : expandAll}
            className={cn(
              'cyber-button-secondary px-4 py-2 text-sm flex items-center gap-2',
              'transition-all duration-300'
            )}
            aria-label={allExpanded ? 'Collapse all steps' : 'Expand all steps'}
          >
            {allExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" aria-hidden="true" />
                <span>Collapse All</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
                <span>Expand All</span>
              </>
            )}
          </button>
        </div>

        {/* Steps Timeline */}
        <div
          className="space-y-0"
          role="list"
          aria-label="Token revival process steps"
        >
          {stepsData.map((step, index) => (
            <div key={step.id} role="listitem">
              {/* Connector line (not before first item) */}
              {index > 0 && (
                <ConnectorLine
                  color={step.color}
                  isReducedMotion={prefersReducedMotion}
                  delay={(index - 1) * 100 + 50}
                />
              )}

              {/* Step Card */}
              <StepCard
                step={step}
                isExpanded={expandedSteps.has(step.id)}
                onToggle={() => toggleStep(step.id)}
                index={index}
                isReducedMotion={prefersReducedMotion}
              />
            </div>
          ))}
        </div>

        {/* Loop Indicator */}
        <LoopIndicator isReducedMotion={prefersReducedMotion} />

        {/* Info Card - Built for Transparency */}
        <div
          className={cn(
            'mt-12 glass-card border border-cyber-cyan/30 p-6',
            !prefersReducedMotion && 'animate-fade-in'
          )}
          style={{ animationDelay: prefersReducedMotion ? '0ms' : '1100ms' }}
        >
          <div className="flex items-start space-x-4">
            <div
              className="w-10 h-10 bg-cyber-cyan/20 rounded-lg flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <Info className="w-5 h-5 text-cyber-cyan" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-cyber-cyan mb-2 font-header">
                Built for Transparency & Security
              </h3>
              <p className="text-text-secondary leading-relaxed text-sm sm:text-base">
                All transactions are on-chain and verifiable. Smart contracts handle governance
                automatically with built-in safety mechanisms including{' '}
                <span className="text-cyber-green font-semibold">quorum requirements (30% participation)</span>,{' '}
                <span className="text-cyber-purple font-semibold">majority voting</span>,{' '}
                <span className="text-cyber-magenta font-semibold">anti-whale caps (20% max voting power)</span>,{' '}
                <span className="text-cyber-cyan font-semibold">proportional fee collection (1%)</span>, and{' '}
                <span className="text-cyber-green font-semibold">recovery proposals</span> to protect community funds.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorksInfographic
